import { useCallback, useContext, useState } from 'react';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import DisplayTable from '../../utility/DisplayTable';
import { Button, Typography, Box, Stack, MenuItem, TextField, Alert, IconButton, Checkbox } from '@mui/material';
import { DownArrowIcon, cropClasses, miscClasses, popupClasses } from '../../utility';
import DeleteIcon from '@mui/icons-material/Delete';
import apiService from '../../../api/apiService';
import { useParams } from 'react-router';
import { FeedbackContext } from '../../../App';

export default function AttachNewTaskPopup(props) {
  const { currDarkThemeClass, open, onClose, parentValues: routineValues2, farm, fetchPhasesData, onRoutinesPopupClose, routineData } = props;
  const feedbackCX = useContext(FeedbackContext);
  const { cropplanid } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [selectedHarvest, setSelectedHarvest] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [taskList, setTaskList] = useState([]);
  const [selectedSourcePackages, setSelectedSourcePackages] = useState(() => {
    return routineData?.source_packages?.map(pkg => pkg.id) || [];
  });
  const [selectedLabPackages, setSelectedLabPackages] = useState(() => {
    return routineData?.lab_testing_packages?.map(pkg => pkg.id) || [];
  });
  const [transferName, setTransferName] = useState('');
  const [transferNote, setTransferNote] = useState('');

  const routineValues = props?.parentValues;

  const titleText = `Attach Task to ${routineValues?.name || 'Routine'}`;
  const headerText = routineValues
    ? `This will attach a new task to the routine: ${routineValues.name}`
    : 'Attach a new task to the selected routine.';

  const defRoutine = routineData?.default_routines?.find(def => def.id === routineValues.default_routine);
  const isTestingLotCreation = defRoutine?.task_level === 7 && defRoutine?.name === 'Testing Lot Creation';
  const isLabTesting = defRoutine?.name === 'Submit for Lab Testing' || defRoutine?.task_level === 8; // here =======
  const isTransferRoutine = defRoutine?.task_level === 9;

  // Filter buckets by routine_type for task_level 7
  const getFilteredBuckets = useCallback(() => {
    if (!routineData?.buckets?.length) return [];

    if (defRoutine?.task_level === 7 && !isTestingLotCreation) {
      return routineData.buckets.filter(bucket => bucket.routine_type === 'fine_trimming');
    }

    return routineData.buckets;
  }, [routineData, defRoutine, isTestingLotCreation]);

  const filteredBuckets = getFilteredBuckets();

  // Check if buckets exist and should be used instead of harvests
  const hasBuckets = filteredBuckets.length > 0;

  const getFilteredItems = useCallback(() => {
    if (!routineData?.strains?.length || !routineData?.items?.length) return [];

    const strainFarmstrainId = routineData.strains[0]?.farmstrain;
    return routineData.items.filter(item => item.strain_id === strainFarmstrainId);
  }, [routineData]);

  const filteredItems = getFilteredItems();

  const handleSourcePackageToggle = packageId => {
    setSelectedSourcePackages(prev => {
      if (prev.includes(packageId)) {
        return prev.filter(id => id !== packageId);
      } else {
        return [...prev, packageId];
      }
    });
  };

  const handleLabPackageToggle = packageId => {
    setSelectedLabPackages(prev => {
      if (prev.includes(packageId)) {
        return prev.filter(id => id !== packageId);
      } else {
        return [...prev, packageId];
      }
    });
  };

  const getItemsLevel = taskLevel => {
    if (!routineData) return [];

    switch (taskLevel) {
      case 0:
        return routineData.batches?.map(batch => batch.id) ?? [];

      case 1:
        return [];

      case 2:
        return routineData.strains?.map(strain => strain.id) ?? [];

      case 5:
        return routineData.harvests?.map(h => h.id) ?? [];

      case 6:
        return routineData.buckets?.map(b => b.id) ?? [];

      case 7:
        if (isTestingLotCreation) {
          return selectedSourcePackages.map(packageId => {
            const sourcePackage = routineData.source_packages.find(pkg => pkg.id === packageId);
            return {
              source_package_id: packageId,
              item: sourcePackage?.item,
              harvest: sourcePackage?.harvests,
              metrc_package_id: sourcePackage?.metrc_package_id,
              weight: sourcePackage?.package_weight,
            };
          });
        } else {
          return getFormattedSelectedItems();
        }

      case 8:
        return selectedLabPackages;

      case 9:
        if (isLabTesting) {
          return selectedLabPackages;
        }
        return [null];

      default:
        return [];
    }
  };

  const getFormattedSelectedItems = () => {
    if (hasBuckets && selectedBuckets.length > 0) {
      // Group by item only (not harvest-item combination)
      const itemGroups = {};

      selectedBuckets.forEach(bucket => {
        const key = bucket.item; // Only group by item, not harvest-item combo

        if (itemGroups[key]) {
          if (!itemGroups[key].harvests.includes(bucket.harvests)) {
            itemGroups[key].harvests.push(bucket.harvests);
          }
          itemGroups[key].buckets.push(bucket.id);
        } else {
          itemGroups[key] = {
            selected_item: bucket.item,
            harvests: [bucket.harvests],
            buckets: [bucket.id],
          };
        }
      });

      return Object.values(itemGroups).map(group => ({
        harvest: group.harvests.join('|'), // Combine multiple harvests with |
        selected_item: group.selected_item,
        buckets: group.buckets.join('|'), // All buckets for this item
      }));
    } else {
      // For harvest-item combinations
      const itemGroups = {};

      taskList.forEach(task => {
        if (itemGroups[task.itemId]) {
          itemGroups[task.itemId].harvests.push(task.harvestId);
        } else {
          itemGroups[task.itemId] = {
            selected_item: task.itemId,
            harvests: [task.harvestId],
          };
        }
      });

      return Object.values(itemGroups).map(group => ({
        harvest: group.harvests.join('|'),
        selected_item: group.selected_item,
      }));
    }
  };

  // Bucket handlers
  const handleAddBucket = () => {
    if (!selectedBucket) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Please select a bucket.',
        autoHideDuration: 3000,
      });
      return;
    }

    const bucketToAdd = filteredBuckets.find(b => b.id === parseInt(selectedBucket));

    if (!bucketToAdd) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Selected bucket not found.',
        autoHideDuration: 3000,
      });
      return;
    }

    // Check if bucket is already selected
    const existingBucket = selectedBuckets.find(bucket => bucket.id === bucketToAdd.id);

    if (existingBucket) {
      feedbackCX.setContextValue(true, {
        type: 'warning',
        message: 'This bucket is already selected.',
        autoHideDuration: 3000,
      });
      return;
    }

    setSelectedBuckets(prev => [...prev, bucketToAdd]);
    setSelectedBucket('');
  };

  const handleRemoveBucket = bucketId => {
    setSelectedBuckets(prev => prev.filter(bucket => bucket.id !== bucketId));
  };

  const handleAddTask = () => {
    if (!selectedHarvest || !selectedItem) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Please select both harvest and item.',
        autoHideDuration: 3000,
      });
      return;
    }

    const harvestName = routineData.harvests?.find(h => h.id === parseInt(selectedHarvest))?.harvest_name || `Harvest ${selectedHarvest}`;
    const itemName = filteredItems?.find(i => i.id === parseInt(selectedItem))?.item_name || `Item ${selectedItem}`;

    const existingTask = taskList.find(task => task.harvestId === parseInt(selectedHarvest) && task.itemId === parseInt(selectedItem));

    if (existingTask) {
      feedbackCX.setContextValue(true, {
        type: 'warning',
        message: 'This harvest-item combination already exists.',
        autoHideDuration: 3000,
      });
      return;
    }

    const newTask = {
      id: Date.now(),
      harvestId: parseInt(selectedHarvest),
      itemId: parseInt(selectedItem),
      harvestName,
      itemName,
      taskName: `Task ${taskList.length + 1}`,
    };

    setTaskList(prev => [...prev, newTask]);
    setSelectedHarvest('');
    setSelectedItem('');
  };

  // here renderLabPackagesSelection ============
  const renderLabPackagesSelection = () => {
    if (!routineData?.lab_testing_packages?.length) {
      return (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            '& .MuiAlert-icon': { color: 'rgba(255, 152, 0, 1)' },
          }}
        >
          No lab packages available for testing.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Transfer Name"
            value={transferName}
            onChange={e => setTransferName(e.target.value)}
            className={`${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`}
            autoComplete="off"
          />
          <TextField
            fullWidth
            label="Transfer Note (Optional)"
            value={transferNote}
            onChange={e => setTransferNote(e.target.value)}
            className={`${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`}
            autoComplete="off"
          />
        </Stack>

        <Typography sx={{ color: 'white', fontSize: '16px', mb: 2, fontWeight: 500 }}>All available lab packages.</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.5,
            maxHeight: '300px',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.3)' },
          }}
        >
          {routineData.lab_testing_packages.map(labPackage => {
            const sourcePackages = labPackage.source_packages || [];
            const sourcePackage = routineData?.source_packages?.find(sp => sourcePackages.includes(sp.id));
            const item = routineData?.items?.find(i => sourcePackage && i.id === sourcePackage.item);

            return (
              <Box
                key={labPackage.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(76, 175, 80, 0.15)',
                  border: '1px solid rgba(76, 175, 80, 0.5)',
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ color: 'white', fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {labPackage.metrc_sub_package_tag_number || 'N/A'}
                  </Typography>
                  {sourcePackage && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Src: {sourcePackage.metrc_package_tag_number}
                      </Typography>
                      {item && (
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.item_name}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const handleRemoveTask = taskId => {
    setTaskList(prev => prev.filter(task => task.id !== taskId));
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const textFieldProps = (value, onChange, label, options, disabled = false) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    label,
    value,
    onChange: e => onChange(e.target.value),
    select: true,
    disabled,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
    children: options,
  });

  const availableBuckets = filteredBuckets.filter(bucket => !selectedBuckets.find(selected => selected.id === bucket.id)) || [];

  const bucketOptions = [
    ...availableBuckets.map(bucket => {
      const item = routineData?.items?.find(item => item.id === bucket.item);
      const harvest = routineData?.harvests?.find(h => h.id === bucket.harvests);

      return (
        <MenuItem key={bucket.id} value={bucket.id} {...cropPlanMenuItemProps}>
          {bucket.name} - {item?.item_name || 'Unknown Item'} (Harvest: {harvest?.harvest_name || bucket.harvests})
        </MenuItem>
      );
    }),
    ...(availableBuckets.length === 0
      ? [
          <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
            {selectedBuckets.length > 0 ? 'All fine trimming buckets selected' : 'No Available Fine Trimming Buckets'}
          </MenuItem>,
        ]
      : []),
  ];

  const harvestOptions = [
    ...(routineData?.harvests?.map(harvest => (
      <MenuItem key={harvest.id} value={harvest.id} {...cropPlanMenuItemProps}>
        {harvest.harvest_name}
      </MenuItem>
    )) || []),
    ...(!routineData?.harvests?.length
      ? [
          <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
            No Available Harvests
          </MenuItem>,
        ]
      : []),
  ];

  const itemOptions = [
    ...(filteredItems?.map(item => (
      <MenuItem key={item.id} value={item.id} {...cropPlanMenuItemProps}>
        {item.item_name}
      </MenuItem>
    )) || []),
    ...(!filteredItems?.length
      ? [
          <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
            No Available Items for this Strain
          </MenuItem>,
        ]
      : []),
  ];

  const tableTitles =
    hasBuckets && selectedBuckets.length > 0
      ? [{ title: 'Task' }, { title: 'Buckets' }, { title: 'Harvests' }, { title: 'Item' }, { title: 'Actions' }]
      : [{ title: 'Task' }, { title: 'Harvest' }, { title: 'Item' }, { title: 'Actions' }];

  const getTableEntries = () => {
    if (hasBuckets && selectedBuckets.length > 0) {
      const itemGroups = {};

      selectedBuckets.forEach(bucket => {
        const item = routineData?.items?.find(item => item.id === bucket.item);
        const harvest = routineData?.harvests?.find(h => h.id === bucket.harvests);

        const itemKey = bucket.item; // Group by item ID

        if (itemGroups[itemKey]) {
          // Add to existing item group
          if (!itemGroups[itemKey].buckets.includes(bucket.name)) {
            itemGroups[itemKey].buckets.push(bucket.name);
          }
          if (!itemGroups[itemKey].harvests.includes(harvest?.harvest_name || `Harvest ${bucket.harvests}`)) {
            itemGroups[itemKey].harvests.push(harvest?.harvest_name || `Harvest ${bucket.harvests}`);
          }
          itemGroups[itemKey].bucketObjects.push(bucket);
        } else {
          // Create new item group
          itemGroups[itemKey] = {
            itemName: item?.item_name || 'Unknown Item',
            buckets: [bucket.name],
            harvests: [harvest?.harvest_name || `Harvest ${bucket.harvests}`],
            bucketObjects: [bucket],
          };
        }
      });

      // Convert to table entries
      return Object.entries(itemGroups).map(([itemId, group], index) => [
        `Task ${index + 1}`,
        group.buckets.join(', '), // Show all buckets for this item
        group.harvests.join(', '), // Show all harvests for this item
        group.itemName,
        <IconButton
          onClick={() => {
            // Remove all buckets for this item group
            group.bucketObjects.forEach(bucket => handleRemoveBucket(bucket.id));
          }}
          size="small"
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon />
        </IconButton>,
      ]);
    } else {
      // Group tasks by item to show combined harvests (for harvest-item combinations)
      const itemGroups = {};

      taskList.forEach(task => {
        if (itemGroups[task.itemId]) {
          itemGroups[task.itemId].harvests.push(task.harvestName);
          itemGroups[task.itemId].tasks.push(task);
        } else {
          itemGroups[task.itemId] = {
            itemName: task.itemName,
            harvests: [task.harvestName],
            tasks: [task],
          };
        }
      });

      return Object.entries(itemGroups).map(([itemId, group], index) => [
        `Task ${index + 1}`,
        group.harvests.join(', '),
        group.itemName,
        <IconButton
          onClick={() => {
            group.tasks.forEach(task => handleRemoveTask(task.id));
          }}
          size="small"
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon />
        </IconButton>,
      ]);
    }
  };

  // Updated function to display tasks in sentence format
  const getPotentialPackagesInfo = () => {
    if (hasBuckets && selectedBuckets.length > 0) {
      // Group by item to show one task per item (like the table display)
      const itemGroups = {};

      selectedBuckets.forEach(bucket => {
        const item = routineData?.items?.find(item => item.id === bucket.item);

        if (itemGroups[bucket.item]) {
          itemGroups[bucket.item].bucketNames.push(bucket.name);
          itemGroups[bucket.item].buckets.push(bucket);
        } else {
          itemGroups[bucket.item] = {
            item: item?.item_name || 'Unknown Item',
            bucketNames: [bucket.name],
            buckets: [bucket],
          };
        }
      });

      return Object.values(itemGroups);
    }
    return null;
  };

  const potentialPackages = getPotentialPackagesInfo();

  const renderSourcePackagesSelection = () => {
    if (!routineData?.source_packages?.length) {
      return (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            '& .MuiAlert-icon': { color: 'rgba(255, 152, 0, 1)' },
          }}
        >
          No source packages available for testing lot creation.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ color: 'white', fontSize: '16px', mb: 2 }}>Select source packages for testing lot creation:</Typography>

        <Box sx={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderRadius: 1, p: 2 }}>
          {routineData.source_packages.map(sourcePackage => {
            const item = routineData?.items?.find(item => item.id === sourcePackage.item);
            const harvest = routineData?.harvests?.find(h => h.id === sourcePackage.harvests);

            return (
              <Box
                key={sourcePackage.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 2,
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={selectedSourcePackages.includes(sourcePackage.id)}
                  onChange={() => handleSourcePackageToggle(sourcePackage.id)}
                  sx={{
                    color: 'rgba(76, 175, 80, 1)',
                    '&.Mui-checked': {
                      color: 'rgba(76, 175, 80, 1)',
                    },
                    mt: 0.5,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: 'white', fontSize: '14px' }}>
                    <strong>Package:</strong> {sourcePackage.metrc_package_tag_number}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    <strong>Item:</strong> {item?.item_name || 'Unknown Item'} |<strong> Harvest:</strong>{' '}
                    {harvest?.harvest_name || `Harvest ${sourcePackage.harvests}`}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {selectedSourcePackages.length > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
            <Typography sx={{ color: 'white', fontSize: '14px', mb: 1 }}>Testing lots will be created for:</Typography>
            <Typography sx={{ color: 'rgba(76, 175, 80, 1)', fontSize: '12px' }}>
              {selectedSourcePackages.length} selected source package{selectedSourcePackages.length > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const handleAttachTask = useCallback(async () => {
    if (!routineValues || !routineData) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Missing routine information.',
        autoHideDuration: 3000,
      });
      return;
    }

    const defRoutine = routineData.default_routines.find(def => def.id === routineValues.default_routine);
    if (!defRoutine) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Default routine not found.',
        autoHideDuration: 3000,
      });
      return;
    }

    let selectedItems;

    if (defRoutine.task_level === 7) {
      if (isTestingLotCreation) {
        if (selectedSourcePackages.length === 0) {
          feedbackCX.setContextValue(true, {
            type: 'error',
            message: 'Please select at least one source package for testing lot creation.',
            autoHideDuration: 3000,
          });
          return;
        }
      } else if (hasBuckets && selectedBuckets.length === 0) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: 'Please select at least one fine trimming bucket.',
          autoHideDuration: 3000,
        });
        return;
      } else if (!hasBuckets && taskList.length === 0) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: 'Please add at least one harvest-item combination.',
          autoHideDuration: 3000,
        });
        return;
      }
      selectedItems = getItemsLevel(defRoutine.task_level);
    } else if (isLabTesting) {
      if (selectedLabPackages.length === 0) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: 'Please select at least one lab package.',
          autoHideDuration: 3000,
        });
        return;
      }
      selectedItems = getItemsLevel(defRoutine.task_level);
    } else {
      selectedItems = getItemsLevel(defRoutine.task_level);
    }

    if (!selectedItems.length) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'No items found for this task level.',
        autoHideDuration: 3000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const hasBucketsItems =
        Array.isArray(selectedItems) &&
        selectedItems.length > 0 &&
        selectedItems.every(item => item && item.harvest && item.selected_item) &&
        selectedItems.some(item => item && item.buckets);

      const hasSourcePackageItems =
        Array.isArray(selectedItems) && selectedItems.length > 0 && selectedItems.some(item => item && item.source_package_id);

      let sourceType;
      if (defRoutine?.task_level === 9) {
        sourceType = 'transfer';
      } else if (hasSourcePackageItems) {
        sourceType = 'parent';
      } else if (hasBucketsItems) {
        sourceType = 'bucket_final_trim';
      } else {
        sourceType = 'harvest_dry_weight';
      }

      const dataToSend = {
        selected_routine: routineValues?.default_routine,
        selected_subphase: routineValues?.subphase_id,
        selected_items: selectedItems,
        cropplan_id: cropplanid,
        routine_level: defRoutine?.task_level,
        source_type: sourceType,
        existing_routine_id: routineValues?.id,
        start_date: routineValues?.subphase?.actual_start_date,
        end_date: routineValues?.subphase?.actual_end_date,
        transfer_name: transferName,
        transfer_note: transferNote,
      };

      console.log('Data to send:', dataToSend);

      const response = await apiService.createInstance('cropplan-attach-routine/?routine-exists=1', dataToSend);

      if (response.status === 200) {
        onClose();
        onRoutinesPopupClose();
        fetchPhasesData?.();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Task attached to the routine successfully.',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error('Error attaching task:', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: error.response?.data?.error || 'Error attaching task to routine.',
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    routineValues,
    routineData,
    selectedBuckets,
    taskList,
    hasBuckets,
    selectedSourcePackages,
    isTestingLotCreation,
    isLabTesting,
    selectedLabPackages,
    transferName,
    transferNote,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const entityDetailPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    headContentText: headerText,
    title: titleText,
    modalMaxWidth: '70vw',
    toShowSave: false,
    toShowCreate: false,
  };

  const renderSelectors = () => {
    if (isTestingLotCreation) {
      return renderSourcePackagesSelection();
    }

    if (isLabTesting) {
      return renderLabPackagesSelection();
    }

    if (defRoutine?.task_level !== 7) {
      return null;
    }

    if (hasBuckets) {
      const addBtnProps = {
        variant: 'contained',
        onClick: handleAddBucket,
        disabled: !selectedBucket || availableBuckets.length === 0,
        className: `${popupClasses?.entityDetail?.paper} ${currDarkThemeClass} customScroll`,
      };

      return (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: 'white', fontSize: '16px', mb: 2 }}>Select fine trimming buckets to create packages:</Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'flex-end' }}>
            <TextField
              {...textFieldProps(selectedBucket, setSelectedBucket, 'Select Fine Trimming Bucket', bucketOptions, availableBuckets.length === 0)}
              sx={{ flex: 1 }}
            />

            <Button {...addBtnProps} sx={{ height: '56px' }} variant="outlined">
              Add Bucket
            </Button>
          </Stack>

          {selectedBuckets.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ color: 'white', fontSize: '14px', mb: 2 }}>Selected Buckets:</Typography>
              <DisplayTable
                entity="Bucket"
                tableEntries={getTableEntries()}
                tableTitles={tableTitles}
                toShowAddBtn={false}
                enablePagination={false}
                currDarkThemeClass={currDarkThemeClass}
                emptyTableMsg="No buckets selected yet"
              />
            </Box>
          )}

          {potentialPackages && potentialPackages.length > 0 && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
              <Typography sx={{ color: 'white', fontSize: '14px', mb: 2 }}>Tasks to be created:</Typography>
              {potentialPackages.map((pkg, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography sx={{ color: 'rgba(76, 175, 80, 1)', fontSize: '12px' }}>
                    <strong>Task {index + 1}</strong> for <strong>{pkg.item}</strong> will be created using bucket
                    {pkg.bucketNames.length > 1 ? 's' : ''} <strong>{pkg.bucketNames.join(', ')}</strong>
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {filteredBuckets.length === 0 && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: 'rgba(255, 152, 0, 1)',
                '& .MuiAlert-icon': { color: 'rgba(255, 152, 0, 1)' },
              }}
            >
              No fine trimming buckets available for this routine.
            </Alert>
          )}
        </Box>
      );
    } else {
      if (!routineData?.harvests?.length) {
        return null;
      }

      const addBtnProps = {
        variant: 'contained',
        onClick: handleAddTask,
        disabled: !selectedHarvest || !selectedItem || filteredItems.length === 0,
        className: `${popupClasses?.entityDetail?.paper} ${currDarkThemeClass} customScroll`,
      };

      return (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: 'white', fontSize: '16px', mb: 2 }}>Select harvest and item combinations:</Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flex: 'row', alignItems: 'center' }}>
            <TextField
              {...textFieldProps(selectedHarvest, setSelectedHarvest, 'Select Harvest', harvestOptions, filteredItems.length === 0)}
              sx={{ flex: 1 }}
            />

            <TextField
              {...textFieldProps(selectedItem, setSelectedItem, 'Select Item', itemOptions, !selectedHarvest || filteredItems.length === 0)}
              sx={{ flex: 1 }}
            />

            <Button {...addBtnProps} sx={{ height: '40px' }}>
              Add
            </Button>
          </Stack>

          {taskList.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ color: 'white', fontSize: '14px', mb: 2 }}>Task List:</Typography>
              <DisplayTable
                entity="Task"
                tableEntries={getTableEntries()}
                tableTitles={tableTitles}
                toShowAddBtn={false}
                enablePagination={false}
                currDarkThemeClass={currDarkThemeClass}
                emptyTableMsg="No tasks added yet"
              />
            </Box>
          )}

          {filteredItems.length === 0 && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: 'rgba(255, 152, 0, 1)',
                '& .MuiAlert-icon': { color: 'rgba(255, 152, 0, 1)' },
              }}
            >
              No items available that match the strain criteria.
            </Alert>
          )}
        </Box>
      );
    }
  };

  const isAttachDisabled = () => {
    if (isLoading || !routineValues) return true;

    if (defRoutine?.task_level === 7) {
      if (isTestingLotCreation) {
        return selectedSourcePackages.length === 0;
      } else if (hasBuckets) {
        return selectedBuckets.length === 0;
      } else {
        return taskList.length === 0;
      }
    }

    if (isLabTesting) {
      return selectedLabPackages.length === 0 || !transferName.trim();
    }

    if (isTransferRoutine) {
      return false;
    }

    return false;
  };

  return (
    <EntityDetailPopup {...entityDetailPopupProps}>
      <Stack spacing={3} sx={{ minHeight: '140px' }}>
        <Box
          sx={{
            textAlign: 'center',
            padding: '10px',
            borderRadius: '8px',
          }}
        >
          <Typography sx={{ color: 'white', fontSize: '16px', lineHeight: 1.5 }}>
            Are you sure you want to attach a new task to this routine? This action will add the task to the existing routine structure.
          </Typography>
        </Box>

        {renderSelectors()}

        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', marginTop: '20px' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isLoading}
            sx={{
              minWidth: '120px',
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAttachTask}
            disabled={isAttachDisabled()}
            sx={{
              minWidth: '120px',
            }}
          >
            {isLoading ? 'Attaching...' : 'Attach Task'}
          </Button>
        </Stack>
      </Stack>
    </EntityDetailPopup>
  );
}
